import { useCallback, useEffect, useRef, useState } from "react";
import { getWsBase, startMicCapture } from "../lib/audio";

const ROOM = "DEMO01";

export function useSpeaker() {
  const [target, setTargetState] = useState("en"); // output language (input auto-detected)
  const [phase, setPhase] = useState("idle"); // idle | connecting | live | error
  const [error, setError] = useState(null);
  const [openaiStatus, setOpenaiStatus] = useState("disconnected");
  const [wsStatus, setWsStatus] = useState("disconnected");
  const [listeners, setListeners] = useState(0);
  const [latencyMs, setLatencyMs] = useState(null);
  const [micLevel, setMicLevel] = useState(0);
  const [micOn, setMicOn] = useState(false);
  const [originalLines, setOriginalLines] = useState([]);
  const [originalPartial, setOriginalPartial] = useState("");
  const [translationLines, setTranslationLines] = useState([]);
  const [translationPartial, setTranslationPartial] = useState("");

  const wsRef = useRef(null);
  const micRef = useRef(null);
  const targetRef = useRef(target);
  const phaseRef = useRef(phase);
  targetRef.current = target;
  phaseRef.current = phase;

  const cleanup = useCallback(() => {
    if (micRef.current) { micRef.current.stop(); micRef.current = null; }
    if (wsRef.current) {
      try { wsRef.current.close(); } catch (e) {}
      wsRef.current = null;
    }
    setMicOn(false);
    setMicLevel(0);
    setWsStatus("disconnected");
    setOpenaiStatus("disconnected");
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const startMic = useCallback(async () => {
    let lastLevelUpdate = 0;
    micRef.current = await startMicCapture((b64, rms) => {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "audio", data: b64 }));
      }
      const now = Date.now();
      if (now - lastLevelUpdate > 120) {
        lastLevelUpdate = now;
        setMicLevel(rms);
      }
    });
    setMicOn(true);
  }, []);

  const start = useCallback(async () => {
    setError(null);
    setPhase("connecting");
    try {
      const ws = new WebSocket(`${getWsBase()}/api/ws/speaker/${ROOM}`);
      wsRef.current = ws;

      ws.onopen = () => {
        setWsStatus("connected");
        ws.send(JSON.stringify({ type: "start", target: targetRef.current }));
      };

      ws.onmessage = async (e) => {
        const msg = JSON.parse(e.data);
        switch (msg.type) {
          case "status":
            setOpenaiStatus(msg.openai);
            setListeners(msg.listeners);
            if (msg.latency_ms != null) setLatencyMs(msg.latency_ms);
            break;
          case "started":
            try {
              await startMic();
              setPhase("live");
            } catch (err) {
              setError("Mikrofon izni alınamadı. Tarayıcı ayarlarından mikrofon erişimine izin verin.");
              setPhase("error");
              ws.send(JSON.stringify({ type: "stop" }));
            }
            break;
          case "latency":
            setLatencyMs(msg.ms);
            break;
          case "original_delta":
            setOriginalPartial((p) => p + msg.delta);
            break;
          case "original_partial":
            setOriginalPartial(msg.text);
            break;
          case "original_done":
            setOriginalLines((l) => [...l, msg.text]);
            setOriginalPartial("");
            break;
          case "translation_delta":
            setTranslationPartial((p) => p + msg.delta);
            break;
          case "translation_partial":
            setTranslationPartial(msg.text);
            break;
          case "translation_done":
            setTranslationLines((l) => [...l, msg.text]);
            setTranslationPartial("");
            break;
          case "error":
            setError(msg.message);
            if (phaseRef.current !== "live") setPhase("error");
            break;
          default:
            break;
        }
      };

      ws.onclose = () => {
        setWsStatus("disconnected");
        if (phaseRef.current === "live" || phaseRef.current === "connecting") {
          if (micRef.current) { micRef.current.stop(); micRef.current = null; }
          setMicOn(false);
          setPhase("error");
          setError((prev) => prev || "Bağlantı koptu. Tekrar deneyin.");
        }
      };

      ws.onerror = () => {
        setError("Sunucu bağlantısı kurulamadı.");
        setPhase("error");
      };
    } catch (err) {
      setError(String(err));
      setPhase("error");
    }
  }, [startMic]);

  const stop = useCallback(() => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "stop" }));
    }
    setPhase("idle");
    setLatencyMs(null);
    cleanup();
  }, [cleanup]);

  const setTarget = useCallback((code) => {
    setTargetState(code);
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "target", target: code }));
    }
  }, []);

  return {
    target, phase, error, openaiStatus, wsStatus, listeners, latencyMs,
    micLevel, micOn, originalLines, originalPartial, translationLines, translationPartial,
    start, stop, setTarget,
  };
}
